import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Box,
  Alert,
  Snackbar,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import verifySmsCode from "../api/app/verifySmsCode";
import sendPasswordReset from "../api/app/sendPasswordReset";
import sendVerification from "../api/app/sendVerification";

const ResetPasswordDialog = ({ open, handleResetPasswordClose, phone: initialPhone }) => {
  const theme = useTheme();

  const getErrorMessage = (err, fallbackMessage) =>
    err?.response?.data?.message ||
    err?.message ||
    (typeof err === "string" ? err : null) ||
    fallbackMessage;

  // 欄位狀態
  const [phone, setPhone] = useState(initialPhone || "");
  const [verificationCode, setVerificationCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordNotMatch, setPasswordNotMatch] = useState(false);

  // 狀態
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, msg: "", severity: "success" });

  // theme-based styles
  const textFieldSx = {
    "& .MuiInputLabel-root": { color: theme.palette.text.secondary },
    "& .MuiInputLabel-root.Mui-focused": { color: theme.palette.info.main },
    "& .MuiOutlinedInput-root": {
      color: theme.palette.text.primary,
      "& fieldset": { borderColor: theme.palette.divider },
      "&:hover fieldset": { borderColor: theme.palette.info.main },
      "&.Mui-focused fieldset": { borderColor: theme.palette.info.main },
    },
  };

  const paperProps = {
    sx: {
      backgroundColor: theme.palette.background.default,
      borderRadius: 2,
      minWidth: { xs: 320, sm: 420 },
    },
  };

  // 同步傳入的 phone
  useEffect(() => {
    if (open && initialPhone) {
      setPhone(initialPhone);
    }
  }, [open, initialPhone]);

  // 清除狀態當對話框關閉
  useEffect(() => {
    if (!open) {
      setVerificationCode("");
      setPassword("");
      setConfirmPassword("");
      setError("");
    }
  }, [open]);

  // 驗證密碼是否一致
  useEffect(() => {
    setPasswordNotMatch(
      password !== "" && confirmPassword !== "" && password !== confirmPassword
    );
  }, [password, confirmPassword]);

  // 倒數計時
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // 重新發送驗證碼
  const handleResendVerification = async () => {
    if (countdown > 0 || !phone) return;
    setLoading(true);
    setError("");
    try {
      const res = await sendVerification(phone);
      if (res) {
        setCountdown(60);
        setSnackbar({ open: true, msg: "驗證碼已重新發送", severity: "success" });
      } else {
        setError("發送失敗，請稍後再試。");
      }
    } catch (err) {
      setError(getErrorMessage(err, "發送失敗，請稍後再試。"));
    } finally {
      setLoading(false);
    }
  };

  // 重設密碼 (2-step: verify SMS code first, then reset password)
  const handleResetPasswordButtonClick = async () => {
    if (!verificationCode) {
      setError("請輸入驗證碼。");
      return;
    }
    if (verificationCode.length !== 6) {
      setError("驗證碼應為 6 位數。");
      return;
    }
    if (!password || !confirmPassword) {
      setError("請輸入新密碼。");
      return;
    }
    if (passwordNotMatch) {
      setError("密碼不一致。");
      return;
    }
    if (password.length < 6) {
      setError("密碼長度至少 6 個字元。");
      return;
    }

    setLoading(true);
    setError("");
    try {
      // Step 1: Verify SMS code - this returns a token
      const verifyResponse = await verifySmsCode(phone, verificationCode);
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.log("verifySmsCode response:", verifyResponse);
      }
      if (!verifyResponse) {
        setError("驗證碼錯誤，請重新輸入。");
        setLoading(false);
        return;
      }

      const token =
        verifyResponse?.token ??
        verifyResponse?.data?.token ??
        verifyResponse?.data?.data?.token ??
        verifyResponse?.access_token;

      if (!token) {
        throw new Error("SMS verified but token missing");
      }

      // Step 2: Force reset password
      const resetResponse = await sendPasswordReset(password, { token });
      if (resetResponse !== null) {
        setSnackbar({ open: true, msg: "密碼變更成功！", severity: "success" });
        handleResetPasswordClose();
      } else {
        setError("密碼重設失敗。");
      }
    } catch (err) {
      console.error(err);
      setError(getErrorMessage(err, "密碼重設失敗，請稍後再試。"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Dialog
        open={open}
        onClose={handleResetPasswordClose}
        PaperProps={paperProps}
      >
        <DialogTitle sx={{ fontWeight: "bold", color: theme.palette.text.primary }}>
          重設密碼
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: theme.palette.text.secondary }}>
            請輸入您收到的驗證碼及新密碼。
          </Typography>

          <TextField
            disabled
            margin="dense"
            id="phone"
            label="手機號碼"
            type="text"
            value={phone}
            fullWidth
            sx={textFieldSx}
          />

          <TextField
            autoFocus
            margin="dense"
            id="verificationCode"
            label="驗證碼"
            type="text"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            fullWidth
            sx={textFieldSx}
            inputProps={{ maxLength: 6 }}
          />

          <Box display="flex" justifyContent="flex-end" mt={0.5} mb={1}>
            <Button
              size="small"
              color="info"
              onClick={handleResendVerification}
              disabled={countdown > 0 || loading}
            >
              {countdown > 0 ? `重新發送 (${countdown}s)` : "重新發送驗證碼"}
            </Button>
          </Box>

          <TextField
            margin="dense"
            id="password"
            label="新密碼"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            sx={textFieldSx}
          />
          <TextField
            margin="dense"
            id="confirmPassword"
            label="確認新密碼"
            type="password"
            fullWidth
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={passwordNotMatch}
            helperText={passwordNotMatch ? "密碼不相同" : ""}
            sx={textFieldSx}
          />

          {error && (
            <Alert icon={false} severity="warning" sx={{ mt: 1 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleResetPasswordClose}
            sx={{
              color: theme.palette.info.main,
              border: `1px solid ${theme.palette.info.main}`,
            }}
          >
            取消
          </Button>
          <Box sx={{ flexGrow: 2 }} />
          <Button
            variant="contained"
            sx={{
              fontWeight: "bold",
              backgroundColor: theme.palette.info.main,
              color: theme.palette.text.selected,
              flexGrow: 1,
            }}
            onClick={handleResetPasswordButtonClick}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : "確認"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ResetPasswordDialog;
